import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import useOutsideClick from "../../hooks/useOutsideClick";
import { toast } from "react-toastify";
import { api } from "../../actions/api";

export default function CreateGroupModal({
  modalId = "createPostModal",
  initialData = null,
  onSubmit = null,
  isEditMode = false,
  onGroupCreated = null,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("Open Discussion");
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [groupName, setGroupName] = useState("");
  const [about, setAbout] = useState("");
  const [members, setMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Add loading state
  const fileInputRef = useRef(null);
  const [errors, setErrors] = useState({
    groupName: "",
    about: "",
    members: "",
    image: "",
  });
  const modalRef = useRef(null);

  // Initialize form with initialData when in edit mode
  useEffect(() => {
    if (isEditMode && initialData) {
      setGroupName(initialData.name || "");
      setAbout(initialData.description || initialData.about || "");

      // Fix: Handle access_type properly
      const accessType = initialData.access_type;
      if (accessType === "Open Discussion") {
        setSelectedOption("Open Discussion");
      } else if (accessType === "Admin Only") {
        setSelectedOption("Admin Only");
      } else {
        setSelectedOption("Open Discussion"); // Default fallback
      }

      // Fix: Handle members properly - check if members exist and have the right structure
      if (initialData.members && Array.isArray(initialData.members)) {
        const formattedMembers = initialData.members
          .filter((member) => member && (member.user || member.id)) // Filter out invalid members
          .map((member) => {
            // Handle both formats: member.user.* and member.*
            const userData = member.user || member;
            return {
              id: userData.id,
              slug: userData.slug,
              image: userData.image,
              full_name: userData.full_name,
            };
          })
          .filter((member) => member.id); // Remove any members without ID

        setMembers(formattedMembers);
      } else {
        setMembers([]);
      }

      // Set image preview if it exists
      if (initialData.image) {
        setImagePreview(initialData.image);
      }
    }
  }, [isEditMode, initialData]);

  // Handle search with API call
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setSearchResults([]);
      return;
    }

    const searchUsers = async () => {
      setIsSearching(true);
      try {
        const res = await api.get(`user/list?search=${searchQuery}`);
        setSearchResults(res.data.data.users || []);
      } catch (error) {
        console.error("Error searching users:", error);
        toast.error("Failed to search users");
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const timer = setTimeout(() => {
      searchUsers();
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Use the outside click hook
  useOutsideClick(modalRef, () => {
    setDropdownOpen(false);
  });

  const handleFileUpload = (event) => {

    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.match("image.*")) {
      toast.error("Please select an image file");
      return;
    }

    // Clean up previous preview if it exists
    if (
      imagePreview &&
      typeof imagePreview === "string" &&
      imagePreview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(imagePreview);
    }

    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    setImageFile(file);
    setErrors((prev) => ({ ...prev, image: "" }));
  };

  const removeImage = (event) => {
    event.stopPropagation();
    if (
      imagePreview &&
      typeof imagePreview === "string" &&
      imagePreview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setImageFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const handleSelect = (value) => {
    setSelectedOption(value);
    setDropdownOpen(false);
  };

  const addMember = (user) => {
    if (!members.some((m) => m.id === user.id)) {
      setMembers([
        ...members,
        {
          id: user.id,
          slug: user.slug,
          image: user.image,
          full_name: user.full_name,
        },
      ]);
      setSearchQuery("");
      setSearchResults([]);
      setErrors((prev) => ({ ...prev, members: "" }));
    }
  };

  const removeMember = (userId) => {
    setMembers(members.filter((member) => member.id !== userId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting) return;

    // Reset errors
    setErrors({
      groupName: "",
      about: "",
      members: "",
      image: "",
    });

    let isValid = true;
    const newErrors = { ...errors };

    // Validate form fields
    if (!groupName.trim()) {
      newErrors.groupName = "Group name is required";
      isValid = false;
    }

    if (!about.trim()) {
      newErrors.about = "About section is required";
      isValid = false;
    }

    if (members.length === 0) {
      newErrors.members = "Please add at least one member";
      isValid = false;
    }

    if (!imageFile && !imagePreview) {
      newErrors.image = "Please upload a group photo";
      isValid = false;
    }

    if (!isValid) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true); // Start loading

    try {
      // Prepare form data object to pass to onSubmit
      const formData = {
        name: groupName,
        about: about,
        access_type: selectedOption, // Keep the exact value from the dropdown
        members: members, // Pass the full member objects
        image: imageFile,
        is_pinned: false, // Default value
      };

      // If editing and no new image file, but has existing image
      if (
        isEditMode &&
        !imageFile &&
        imagePreview &&
        typeof imagePreview === "string" &&
        !imagePreview.startsWith("blob:")
      ) {
        formData.existing_image = imagePreview;
      }



      if (isEditMode && onSubmit) {
        // Pass the data object to the parent component's onSubmit
        await onSubmit(formData);
      } else {
        // Create mode - handle FormData creation here
        const form = new FormData();
        form.append("name", groupName);
        form.append("about", about);
        form.append("access_type", selectedOption);

        // Add members as array
        members.forEach((member) => {
          form.append("members[]", member.id);
        });

        if (imageFile) {
          form.append("image", imageFile);
        }

        const res = await api.post("user/tribe-group", form, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        toast.success("Group created successfully!");

        if (onGroupCreated) {
          onGroupCreated();
        }

        setGroupName("");
        setAbout("");
        setMembers([]);
        setImagePreview(null);
        setImageFile(null);
        setSelectedOption("Open Discussion");
        setErrors({
          groupName: "",
          about: "",
          members: "",
          image: "",
        });

        const modalElement = document.getElementById(modalId);
        if (modalElement) {
          const bootstrapModal =
            window.bootstrap?.Modal?.getInstance(modalElement);
          if (bootstrapModal) {
            bootstrapModal.hide();
          } else {
            modalElement.classList.remove("show");
            modalElement.style.display = "none";
            document.body.classList.remove("modal-open");
            const backdrop = document.querySelector(".modal-backdrop");
            if (backdrop) backdrop.remove();
          }
        }
      }
    } catch (error) {
      console.error("Error creating/updating group:", error);
      toast.error(
        error.response?.data?.message ||
          `Failed to ${isEditMode ? "update" : "create"} group`,
      );
    } finally {
      setIsSubmitting(false); // Stop loading
    }
  };

  // Clean up object URL when component unmounts
  useEffect(() => {
    return () => {
      if (
        imagePreview &&
        typeof imagePreview === "string" &&
        imagePreview.startsWith("blob:")
      ) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  return (
    <div
      className="modal add-task-modal fade"
      id={modalId}
      tabIndex={-1}
      aria-labelledby="addNewTaskModalLabel"
      aria-hidden="true"
      ref={modalRef}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h1 className="modal-title text-heading-h3">
              {isEditMode ? "Edit Group" : "Create Group"}
            </h1>
          </div>
          <button
            type="button"
            className="btn-close btn-close-white position-absolute end-0 me-3"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
          <hr className="modal-hr" />
          <div className="modal-body">
            <form className="add-task-form" onSubmit={handleSubmit}>
              <div className="row">
                {/* Group Name Field */}
                <div className="col-md-12">
                  <div
                    className="textfield-1"
                    style={{
                      border: `1px solid var(--Text-200, #c8ced3)`,
                      borderRadius: "12px",
                    }}
                  >
                    <input
                      className="text-body-b2"
                      type="text"
                      placeholder=""
                      value={groupName}
                      onChange={(e) => {
                        setGroupName(e.target.value);
                        if (errors.groupName) {
                          setErrors((prev) => ({ ...prev, groupName: "" }));
                        }
                      }}
                    />
                    <label
                      className="text-boby-b2"
                      style={{ background: "#132534" }}
                    >
                      Group Name
                    </label>
                  </div>
                  {errors.groupName && (
                    <div className="text-danger mt-1 text-small-s3">
                      {errors.groupName}
                    </div>
                  )}
                </div>

                {/* About Field */}
                <div className="col-md-12">
                  <div className="textfield-1">
                    <textarea
                      className="text-body-b2"
                      placeholder=""
                      name="about"
                      value={about}
                      onChange={(e) => {
                        setAbout(e.target.value);
                        if (errors.about) {
                          setErrors((prev) => ({ ...prev, about: "" }));
                        }
                      }}
                    ></textarea>
                    <label className="text-body-b2">Write About</label>
                  </div>
                  {errors.about && (
                    <div className="text-danger mt-1 text-small-s3">
                      {errors.about}
                    </div>
                  )}
                </div>

                {/* Members Selection */}
                <div className="col-md-12">
                  <div className="textfield-1">
                    <input
                      className="text-body-b2"
                      type="text"
                      placeholder="Add members"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <label
                      className="text-boby-b2"
                      style={{ background: "#132534" }}
                    >
                      Add Members
                    </label>
                  </div>

                  {isSearching ? (
                    <div className="text-center py-2">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden" style={{ color: "#fff"}}>Loading...</span>
                      </div>
                    </div>
                  ) : (
                    searchResults.length > 0 && (
                      <div
                        className="search-results-dropdown"
                        style={{
                          maxHeight: "200px",
                          overflowY: "auto",
                          backgroundColor: "#1b2c3a",
                          borderRadius: "8px",
                          marginTop: "8px",
                          border: "1px solid #2a4052",
                        }}
                      >
                        {searchResults.map((user) => (
                          <div
                            key={user.id}
                            className="search-result-item"
                            onClick={() => addMember(user)}
                            style={{
                              padding: "8px 12px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              borderBottom: "1px solid #2a4052",
                            }}
                          >
                            <img
                              src={user.image || "/icons/default-user.svg"}
                              alt={user.slug}
                              style={{
                                width: "30px",
                                height: "30px",
                                borderRadius: "50%",
                                objectFit: "cover",
                              }}
                            />
                            <div style={{ fontWeight: "500", color: "#fff" }}>
                              @{user.slug}
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  )}

                  {/* Selected Members */}
                  <div
                    className="add-member-section"
                    style={{
                      background: "#132534",
                      marginTop: "16px",
                      borderRadius: "8px",
                      padding: "12px",
                      border: "1px solid #2a4052",
                    }}
                  >
                    <div style={{ marginBottom: "8px", fontWeight: "500" , color: "#fff"}}>
                      Selected Members ({members.length})
                    </div>
                    <div
                      className="member-container"
                      style={{
                        minHeight: "60px",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px",
                      }}
                    >
                      {members.length > 0 ? (
                        members.map((member) => (
                          <div
                            key={member.id}
                            className="member-chip"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              backgroundColor: "#2a4052",
                              padding: "6px 12px",
                              borderRadius: "20px",
                              width: " fit-content",
                            }}
                          >
                            <img
                              src={member.image || "/icons/default-user.svg"}
                              alt={member.slug || member.full_name}
                              style={{
                                width: "24px",
                                height: "24px",
                                borderRadius: "50%",
                                objectFit: "cover",
                              }}
                            />
                            <span style={{ fontSize: "14px" }}>
                              {/* Fix: Display slug or full_name with proper fallback */}
                              {member.slug
                                ? `@${member.slug}`
                                : member.full_name || `User ${member.id}`}
                            </span>
                            <img
                              src="icons/cross.svg"
                              style={{
                                width: "12px",
                                height: "12px",
                                cursor: "pointer",
                                marginLeft: "4px",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeMember(member.id);
                              }}
                              alt="Remove"
                            />
                          </div>
                        ))
                      ) : (
                        <div
                          className="text-body-b4"
                          style={{ color: "#7E8897" }}
                        >
                          No members added yet
                        </div>
                      )}
                    </div>
                  </div>
                  {errors.members && (
                    <div className="text-danger mt-1 text-small-s3">
                      {errors.members}
                    </div>
                  )}
                </div>

                {/* Group Photo Upload */}
                <div className="col-md-12">
                  <div style={{ marginBottom: "-14px" }}>
                    <span
                      className="squads-message-title"
                      style={{ fontWeight: "400", fontSize: "14px" }}
                    >
                      Group Photo
                    </span>
                  </div>
                  <br />
                  <div
                    className="d-flex flex-column justify-content-center align-items-center"
                    style={{
                      border: "3px dotted rgba(27, 44, 58, 1)",
                      borderRadius: "24px",
                      width: "100%",
                      height: imagePreview ? "300px" : "150px",
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden",
                      backgroundColor: imagePreview
                        ? "transparent"
                        : "rgba(27, 44, 58, 0.5)",
                    }}
                    onClick={() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.click();
                      }
                    }}
                  >
                    {imagePreview ? (
                      <>
                        <img
                          src={imagePreview}
                          alt="Group preview"
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <img
                          src="icons/close-btn.svg"
                          alt="Remove image"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImage(e);
                          }}
                          style={{
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                            width: "24px",
                            height: "24px",
                            cursor: "pointer",
                            zIndex: 10,
                            backgroundColor: "#2A4052",
                            borderRadius: "50%",
                            padding: "4px",
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "60px",
                            height: "60px",
                            backgroundColor: "rgba(27, 44, 58, 1)",
                            borderRadius: "50%",
                          }}
                        >
                          <img
                            src="/icons/camera.svg"
                            alt="camera icon"
                            style={{ width: "24px", height: "24px" }}
                          />
                        </div>
                        <div
                          style={{
                            textAlign: "center",
                            marginTop: "12px",
                          }}
                        >
                          <h1
                            style={{
                              color: "#008CFF",
                              fontSize: "16px",
                              lineHeight: "24px",
                              fontWeight: "400",
                              margin: "0",
                            }}
                          >
                            Upload from Library
                          </h1>
                        </div>
                      </>
                    )}
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      accept="image/*"
                      onChange={handleFileUpload}
                    />
                  </div>
                  {errors.image && (
                    <div className="text-danger mt-1 text-small-s3">
                      {errors.image}
                    </div>
                  )}
                </div>

                {/* Discussion Type */}
                <div className="col-md-12">
                  <div className="textfield-1 position-relative">
                    <input
                      type="text"
                      className="custom-select for-level"
                      placeholder=""
                      value={
                        selectedOption === "Open Discussion"
                          ? "Open Discussion"
                          : "Admin Only"
                      }
                      readOnly
                      onClick={toggleDropdown}
                    />
                    {dropdownOpen && (
                      <div
                        className="custom-select-dropdown1 for-level"
                        style={{
                          backgroundColor: "#1b2c3a",
                          border: "1px solid #2a4052",
                          borderRadius: "8px",
                          padding: "8px 0",
                          zIndex: 1000,
                        }}
                      >
                        {[
                          {
                            value: "Open Discussion",
                            label: "Open Discussion",
                          },
                          { value: "Admin Only", label: "Admin Only" },
                        ].map((option) => (
                          <div
                            key={option.value}
                            className={`text-body-b2 ${
                              selectedOption === option.value ? "active" : ""
                            }`}
                            style={{
                              cursor: "pointer",
                              color: "white",
                              padding: "8px 16px",
                              backgroundColor:
                                selectedOption === option.value
                                  ? "#2a4052"
                                  : "transparent",
                            }}
                            onClick={() => handleSelect(option.value)}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                    <label
                      className="text-boby-b2"
                      style={{ background: "#132534" }}
                    >
                      Discussion Type
                    </label>
                  </div>
                </div>
              </div>
              <div className="d-flex gap-3 mt-4 justify-content-center">
                <button
                  type="submit"
                  className="btn-1 btn-1-primary px-3 text-body-b1"
                  style={{
                    width: "182px",
                    opacity: isSubmitting ? 0.7 : 1,
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                  }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {isEditMode ? "Updating..." : "Creating..."}
                    </>
                  ) : isEditMode ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </button>
                <button
                  type="button"
                  className="btn-1 btn-1-primary px-3 text-body-b1"
                  style={{ width: "182px", background: "#eb5757" }}
                  data-bs-dismiss="modal"
                  disabled={isSubmitting}
                  onClick={() => {
                    if (!isEditMode) {
                      setGroupName("");
                      setAbout("");
                      setMembers([]);
                      setImagePreview(null);
                      setImageFile(null);
                      setSelectedOption("Open Discussion");
                      setErrors({
                        groupName: "",
                        about: "",
                        members: "",
                        image: "",
                      });
                    }
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

CreateGroupModal.propTypes = {
  modalId: PropTypes.string,
  initialData: PropTypes.shape({
    name: PropTypes.string,
    about: PropTypes.string,
    description: PropTypes.string,
    access_type: PropTypes.string,
    members: PropTypes.array,
    image: PropTypes.string,
  }),
  onSubmit: PropTypes.func,
  isEditMode: PropTypes.bool,
  onGroupCreated: PropTypes.func,
};

CreateGroupModal.defaultProps = {
  modalId: "createPostModal",
  initialData: null,
  onSubmit: null,
  isEditMode: false,
  onGroupCreated: null,
};
